import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  Gavel, 
  MessageSquare, 
  Star, 
  FolderHeart,
  Heart,
  Menu,
  X,
  LogOut,
  FolderOpen,
  Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.role !== 'admin' && userData.role !== 'moderator') {
          navigate('/');
          return;
        }
        setUser(userData);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', adminOnly: false },
    { path: '/admin/products', icon: Package, label: 'Products', adminOnly: true },
    { path: '/admin/media', icon: Image, label: 'Media', adminOnly: true },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders', adminOnly: true },
    { path: '/admin/users', icon: Users, label: 'Users', adminOnly: true },
    { path: '/admin/auctions', icon: Gavel, label: 'Auctions', adminOnly: false },
    { path: '/admin/offers', icon: MessageSquare, label: 'Offers', adminOnly: false },
    { path: '/admin/discussions', icon: MessageSquare, label: 'Discussions', adminOnly: false },
    { path: '/admin/reviews', icon: Star, label: 'Reviews', adminOnly: false },
    { path: '/admin/collections', icon: FolderHeart, label: 'Collections', adminOnly: false },
    { path: '/admin/wishlists', icon: Heart, label: 'Wishlists', adminOnly: true },
    { path: '/admin/category', icon: FolderOpen, label: 'Category', adminOnly: true },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    !item.adminOnly || (user && user.role === 'admin')
  );

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-pulse text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-50 ${
        sidebarOpen ? 'w-64' : 'w-20'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          {sidebarOpen && <h1 className="text-xl font-bold text-red-500">CollectrsInc Admin</h1>}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                {user.name[0].toUpperCase()}
              </div>
            )}
            {sidebarOpen && (
              <div>
                <div className="font-medium text-sm">{user.name}</div>
                <div className="text-xs text-gray-400 capitalize">{user.role}</div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-2 flex-1 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive 
                    ? 'bg-red-600 text-white' 
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Icon size={20} />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 w-full transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
